// src/contexts/SeasonalThemeProvider.tsx
// ★ React context that checks the current date and exposes the active season.
// Wraps the application to provide seasonal theme info to all components.
// Re-evaluates at midnight so long-running sessions switch automatically.

import React, { createContext, useState, useEffect, useCallback } from 'react';
import { getActiveSeason, Season } from '../config/seasonalConfig';

export interface SeasonalThemeContextValue {
    /** The currently active season based on the date */
    season: Season;
    /** Whether a seasonal theme (non-default) is active */
    isSeasonalActive: boolean;
    /** Convenience flags for conditional rendering */
    isHalloween: boolean;
    isChristmas: boolean;
}

export const SeasonalThemeContext = createContext<SeasonalThemeContextValue>({
    season: 'default',
    isSeasonalActive: false,
    isHalloween: false,
    isChristmas: false,
});

interface SeasonalThemeProviderProps {
    /** Override the detected season (useful for testing/preview) */
    forceSeason?: Season;
    children: React.ReactNode;
}

/** Build the context value from a season key */
function buildValue(season: Season): SeasonalThemeContextValue {
    return {
        season,
        isSeasonalActive: season !== 'default',
        isHalloween: season === 'halloween',
        isChristmas: season === 'christmas',
    };
}

export const SeasonalThemeProvider: React.FC<SeasonalThemeProviderProps> = ({
    forceSeason,
    children,
}) => {
    const [value, setValue] = useState<SeasonalThemeContextValue>(() =>
        buildValue(forceSeason ?? getActiveSeason()),
    );

    // Re-evaluate whenever forceSeason changes
    useEffect(() => {
        if (forceSeason !== undefined) {
            setValue(buildValue(forceSeason));
        }
    }, [forceSeason]);

    // Schedule a re-check at midnight so long-running sessions auto-switch
    const scheduleNextCheck = useCallback(() => {
        if (forceSeason !== undefined) return;          // skip if overridden
        const now = new Date();
        const midnight = new Date(now);
        midnight.setHours(24, 0, 0, 0);                // next midnight
        const msUntilMidnight = midnight.getTime() - now.getTime();

        const timer = setTimeout(() => {
            setValue(buildValue(getActiveSeason()));
            scheduleNextCheck();                        // chain to next midnight
        }, msUntilMidnight);

        return timer;
    }, [forceSeason]);

    useEffect(() => {
        const timer = scheduleNextCheck();
        return () => { if (timer) clearTimeout(timer); };
    }, [scheduleNextCheck]);

    return (
        <SeasonalThemeContext.Provider value={value}>
            {children}
        </SeasonalThemeContext.Provider>
    );
};

export default SeasonalThemeProvider;
